import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for subscribing to note task completion events via SSE
 *
 * This hook establishes an SSE connection to the backend to receive
 * real-time notifications when AI processing tasks complete for notes.
 *
 * Usage:
 * const { lastEvent, isConnected, connectionError } = useNoteTaskEvents({
 *   onTaskCompleted: (event) => { // refresh note list },
 *   onTaskFailed: (event) => { // show error notification },
 * 
 * 加入重连机制：
 * 第 1 次失败	约 5s 后第 2 次尝试
 * 第 2 次失败	约 7.5s 后第 3 次尝试
 * 第 3 次失败	不再自动重连，展示「已达上限」
 * 401	立即停止重连，提示「登录已过期，请重新登录」
 * 手动 reconnect() 或重新登录	计数清零，再给 3 次尝试
 * });
 */
export const useNoteTaskEvents = (options = {}) => {
    const {
        onTaskCompleted,
        onTaskFailed,
        onNoteUpdated,
        autoReconnect = true,
        reconnectDelay = 5000,
        maxReconnectAttempts = 2,
    } = options;

    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [lastEvent, setLastEvent] = useState(null);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);

    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const attemptCountRef = useRef(0);

    /**
     * Connect to SSE endpoint
     */
    const connect = useCallback(() => {
        // Get auth token
        const authStorage = localStorage.getItem('auth-storage');
        const token = authStorage ? JSON.parse(authStorage).state?.token : null;

        if (!token) {
            console.log('[useNoteTaskEvents] No auth token, skipping SSE connection');
            return;
        }

        // Check token expiry before connecting to avoid unnecessary 401s
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.exp && payload.exp < Date.now() / 1000) {
                console.log('[useNoteTaskEvents] Token expired, skipping SSE connection');
                return;
            }
        } catch (e) {
            // If token can't be parsed, let the server decide
        }

        if (attemptCountRef.current >= maxReconnectAttempts + 1) {
            console.warn('[useNoteTaskEvents] 已达最大连接尝试次数，不再重连');
            return;
        }

        attemptCountRef.current += 1;
        const currentAttempt = attemptCountRef.current;

        // Close existing connection
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        const baseUrl = process.env.REACT_APP_API_URL || '/api';
        const sseUrl = `${baseUrl}/tasks/events`;

        console.log(`[useNoteTaskEvents] Connecting to SSE (attempt ${currentAttempt}/${maxReconnectAttempts + 1}):`, sseUrl);

        // Create EventSource with fetch for authentication
        // Note: Standard EventSource doesn't support headers, so we use fetch with ReadableStream
        const connectWithFetch = async () => {
            try {
                const response = await fetch(sseUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'text/event-stream',
                        'Cache-Control': 'no-cache',
                    },
                });

                if (!response.ok) {
                    const errorText = await response.text().catch(() => 'Unknown error');
                    const err = new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
                    err.status = response.status;
                    throw err;
                }

                // Check if response body exists
                if (!response.body) {
                    throw new Error('Response body is null');
                }

                setIsConnected(true);
                setConnectionError(null);
                setReconnectAttempts(0);
                attemptCountRef.current = 0;
                console.log('[useNoteTaskEvents] SSE connection established');

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                // Store reader reference for cleanup
                eventSourceRef.current = {
                    close: () => {
                        try {
                            reader.cancel();
                        } catch (e) {
                            console.warn('[useNoteTaskEvents] Error canceling reader:', e);
                        }
                    },
                    reader
                };

                while (true) {
                    let readResult;
                    try {
                        readResult = await reader.read();
                    } catch (readError) {
                        console.error('[useNoteTaskEvents] Error reading from stream:', readError);
                        throw readError;
                    }

                    const { done, value } = readResult;

                    if (done) {
                        console.log('[useNoteTaskEvents] SSE stream ended');
                        break;
                    }

                    try {
                        buffer += decoder.decode(value, { stream: true });
                        const lines = buffer.split('\n');

                        // Keep the last incomplete line in buffer
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (line.startsWith('data: ')) {
                                try {
                                    const data = JSON.parse(line.slice(6));
                                    handleEvent(data);
                                } catch (parseError) {
                                    // Might be a heartbeat comment or malformed data
                                    if (!line.startsWith(':')) {
                                        console.warn('[useNoteTaskEvents] Failed to parse SSE data:', parseError);
                                    }
                                }
                            }
                        }
                    } catch (decodeError) {
                        console.error('[useNoteTaskEvents] Error decoding stream data:', decodeError);
                        // Continue reading despite decode error
                    }
                }

                // Connection closed, attempt reconnect
                setIsConnected(false);
                
                // Clean up reader reference
                if (eventSourceRef.current) {
                    eventSourceRef.current = null;
                }
                
                if (autoReconnect) {
                    scheduleReconnect();
                }

            } catch (error) {
                // Enhanced error logging
                const errorMessage = error.message || 'Unknown error';
                const errorName = error.name || 'Error';
                const status = error.status;

                setIsConnected(false);

                // Clean up reader reference on error
                if (eventSourceRef.current) {
                    try {
                        eventSourceRef.current.close();
                    } catch (closeError) {
                        console.warn('[useNoteTaskEvents] Error closing connection on error:', closeError);
                    }
                    eventSourceRef.current = null;
                }

                // 401：token 过期或无效，不再用同一 token 重连，避免刷接口
                if (status === 401) {
                    console.warn('[useNoteTaskEvents] SSE 认证失败(401)，请重新登录');
                    setConnectionError('登录已过期，请重新登录');
                    attemptCountRef.current = maxReconnectAttempts + 1;
                    return;
                }

                if (attemptCountRef.current >= maxReconnectAttempts + 1) {
                    setConnectionError(`连接失败次数已达上限（${maxReconnectAttempts + 1} 次），已停止自动重连`);
                    return;
                }

                console.error('[useNoteTaskEvents] SSE connection error:', {
                    name: errorName,
                    message: errorMessage,
                    status,
                    url: sseUrl,
                    stack: error.stack
                });

                // Provide more descriptive error messages
                let userFriendlyError = errorMessage;
                if (errorMessage.includes('Failed to fetch')) {
                    userFriendlyError = 'Network error: Unable to connect to server. Please check your connection.';
                } else if (errorMessage.includes('HTTP error')) {
                    userFriendlyError = `Server error: ${errorMessage}`;
                } else if (errorMessage.includes('NetworkError') || errorMessage.includes('Network request failed')) {
                    userFriendlyError = 'Network error: Please check your internet connection.';
                }

                setConnectionError(userFriendlyError);

                if (autoReconnect) {
                    scheduleReconnect();
                }
            }
        };

        connectWithFetch();
    }, [autoReconnect]);

    /**
     * Handle incoming SSE event
     */
    const handleEvent = useCallback((event) => {
        console.log('[useNoteTaskEvents] Received event:', event);
        setLastEvent(event);

        switch (event.type) {
            case 'connected':
                console.log('[useNoteTaskEvents] Connection confirmed by server');
                break;

            case 'task_completed':
                console.log('[useNoteTaskEvents] Task completed for note:', event.noteId);
                if (onTaskCompleted) {
                    onTaskCompleted(event);
                }
                break;

            case 'task_failed':
                console.log('[useNoteTaskEvents] Task failed for note:', event.noteId);
                if (onTaskFailed) {
                    onTaskFailed(event);
                }
                break;

            case 'note_updated':
                console.log('[useNoteTaskEvents] Note updated:', event.noteId);
                if (onNoteUpdated) {
                    onNoteUpdated(event);
                }
                break;

            case 'heartbeat':
                // Heartbeat received, connection is alive
                break;

            default:
                console.log('[useNoteTaskEvents] Unknown event type:', event.type);
        }
    }, [onTaskCompleted, onTaskFailed, onNoteUpdated]);

    /**
     * Schedule a reconnection attempt
     * 最多重连 maxReconnectAttempts 次，即共 maxReconnectAttempts+1 次连接尝试（含首次）
     */
    const scheduleReconnect = useCallback(() => {
        if (attemptCountRef.current >= maxReconnectAttempts + 1) {
            console.warn('[useNoteTaskEvents] 已达最大尝试次数，停止重连');
            setConnectionError(`连接失败次数已达上限（${maxReconnectAttempts + 1} 次），已停止自动重连`);
            return;
        }

        const delay = reconnectDelay * Math.pow(1.5, reconnectAttempts);
        const nextAttempt = attemptCountRef.current + 1;
        console.log(`[useNoteTaskEvents] ${delay}ms 后重连 (第 ${nextAttempt} 次尝试 / 最多 ${maxReconnectAttempts + 1} 次)`);

        reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts(prev => prev + 1);
            connect();
        }, delay);
    }, [reconnectAttempts, maxReconnectAttempts, reconnectDelay, connect]);

    /**
     * Disconnect from SSE
     */
    const disconnect = useCallback(() => {
        console.log('[useNoteTaskEvents] Disconnecting from SSE');

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (eventSourceRef.current) {
            try {
                eventSourceRef.current.close();
            } catch (error) {
                console.warn('[useNoteTaskEvents] Error closing connection:', error);
            }
            eventSourceRef.current = null;
        }

        setIsConnected(false);
        // 不断开时保留 attemptCountRef，便于“已达上限”后不再自动连；仅在手动 reconnect() 时清零
    }, []);

    /**
     * Manual reconnect（重置尝试计数后重新连接）
     */
    const reconnect = useCallback(() => {
        setReconnectAttempts(0);
        setConnectionError(null);
        attemptCountRef.current = 0;
        connect();
    }, [connect]);

    // Connect on mount, disconnect on unmount
    useEffect(() => {
        connect();

        return () => {
            disconnect();
        };
    }, [connect, disconnect]);

    // Clean up SSE connection on page unload/refresh
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    // Listen for auth changes to reconnect
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'auth-storage') {
                console.log('[useNoteTaskEvents] Auth storage changed, reconnecting');
                reconnect();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [reconnect]);

    return {
        isConnected,
        connectionError,
        lastEvent,
        reconnectAttempts,
        disconnect,
        reconnect,
    };
};

export default useNoteTaskEvents;
